import React, { useEffect, useMemo, useState } from "react";
import {
  Col,
  Row,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  FormGroup,
} from "reactstrap";
import { H3 } from "../../../../AbstractElements";
import axios from "axios";

interface AssetRequest {
  request_id: number;
  candidate_id: number | null;
  requested_asset_type: string | null;
  ship_to_name: string | null;
  ship_to_email: string | null;
  ship_to_company: string | null;
  shipping_address1: string | null;
  shipping_address2: string | null;
  shipping_city: string | null;
  shipping_state: string | null;
  shipping_postal_code: string | null;
  shipping_country: string | null;
  requested_by_email: string | null;
  needed_by_date: string | null;
  status: string | null;
  shipping_notes: string | null;
  intune_completed: number | null;
  device_ownership_type: string | null;
  ct_verified: number | null;
  service_line: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  scheduled_delivery_date?: string | null;
}

type SortKey = keyof AssetRequest;

interface AssetRequestsReportProps {
  reportTitle?: string;
  filterStatus?: string;
}

const STATUS_OPTIONS = [
  "Requested",
  "Approved",
  "Rejected",
  "Fulfilled",
  "Cancelled",
  "In Progress",
  "Shipped",
];

const SERVICE_LINE_OPTIONS = ["SGA", "Delivery"];

const ASSET_TYPE_OPTIONS = ["Laptop", "Monitor", "Other", "MAC Book"];

const OWNERSHIP_OPTIONS = ["HDM Owned", "HDM Leased", "Legacy"];

const AssetRequestsReport: React.FC<AssetRequestsReportProps> = ({
  reportTitle = "Asset Requests",
  filterStatus,
}) => {
  const [data, setData] = useState<AssetRequest[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [sortConfig, setSortConfig] = useState<{
    key: SortKey;
    direction: "asc" | "desc";
  } | null>(null);

  const [selectedRequest, setSelectedRequest] = useState<AssetRequest | null>(
    null,
  );
  const [editDraft, setEditDraft] = useState<AssetRequest | null>(null);
  const [saving, setSaving] = useState(false);

  const parseDate = (s: string | null | undefined) => {
    if (!s) return null;
    const d = new Date(s.replace(" ", "T"));
    return isNaN(d.getTime()) ? null : d;
  };

  const daysBetween = (start: Date | null, end: Date | null) => {
    if (!start || !end) return null;
    const ms = end.getTime() - start.getTime();
    return Math.floor(ms / (1000 * 60 * 60 * 24));
  };

  const formatIntBool = (value: number | string | null | undefined) => {
    if (Number(value) === 1) return "Yes";
    if (Number(value) === 0) return "No";
    return "—";
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get<AssetRequest[]>(`/list-asset-requests`);
      let rows = response.data ?? [];

      if (filterStatus) {
        rows = rows.filter(
          (r) => (r.status ?? "").toLowerCase() === filterStatus.toLowerCase(),
        );
      }

      setData(rows);
    } catch (err: any) {
      console.error("Error fetching asset requests:", err);
      setError(err?.message ?? "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  const escapeCsvValue = (value: string) => {
    const escaped = value.replace(/"/g, '""');
    return `"${escaped}"`;
  };

  const downloadCsv = () => {
    const rows = sortedData.map((r) => ({
      shipTo: r.ship_to_name ?? "",
      intuneCompleted: Number(r.intune_completed) === 1 ? "Yes" : "No",
      ownership: r.device_ownership_type ?? "",
      ctVerified: Number(r.ct_verified) === 1 ? "Yes" : "No",
      serviceLine: r.service_line ?? "",
      requestedAssetType: r.requested_asset_type ?? "",
      status: r.status ?? "",
      shippingNotes: r.shipping_notes ?? "",
      scheduledDeliveryDate: r.scheduled_delivery_date ?? "",
    }));

    const headers = [
      "Ship To",
      "Asset Type",
      "Status",
      "Intune Completed",
      "Ownership",
      "CT Verified",
      "Service Line",
      "Shipping Notes",
      "Scheduled Delivery Date",
    ];

    const csvLines = [
      headers.join(","),
      ...rows.map((row) =>
        [
          escapeCsvValue(row.shipTo),
          escapeCsvValue(row.requestedAssetType),
          escapeCsvValue(row.status),
          escapeCsvValue(row.intuneCompleted),
          escapeCsvValue(row.ownership),
          escapeCsvValue(row.ctVerified),
          escapeCsvValue(row.serviceLine),
          escapeCsvValue(row.shippingNotes),
          escapeCsvValue(row.scheduledDeliveryDate),
        ].join(","),
      ),
    ];

    const csvContent = csvLines.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "asset-request-shipping-status.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    fetchData();
  }, [filterStatus]);

  const sortedData = useMemo(() => {
    if (!data) return [];
    if (!sortConfig) return data;

    const { key, direction } = sortConfig;

    const getValue = (row: AssetRequest) => {
      const v = row[key];

      if (
        key === "created_at" ||
        key === "updated_at" ||
        key === "needed_by_date" ||
        key === "scheduled_delivery_date"
      ) {
        return parseDate(v as string | null)?.getTime() ?? 0;
      }

      if (key === "request_id" || key === "candidate_id") {
        return Number(v ?? 0);
      }

      if (key === "intune_completed" || key === "ct_verified") {
        if (v === null || v === undefined || v === "") return -1;
        return Number(v);
      }

      return (v ?? "").toString().toLowerCase();
    };

    return [...data].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);

      if (av < bv) return direction === "asc" ? -1 : 1;
      if (av > bv) return direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig]);

  const sortData = (key: SortKey) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const summary = useMemo(() => {
    const rows = sortedData;
    const now = new Date();
    const total = rows.length;

    const byStatus: Record<string, number> = {};
    rows.forEach((r) => {
      const s = (r.status ?? "Unknown").trim() || "Unknown";
      byStatus[s] = (byStatus[s] ?? 0) + 1;
    });

    const openRows = rows.filter(
      (r) =>
        !["fulfilled", "cancelled", "shipped"].includes(
          (r.status ?? "").toLowerCase(),
        ),
    );

    const avgOpenDays = (() => {
      const values = openRows
        .map((r) => daysBetween(parseDate(r.created_at), now))
        .filter((v): v is number => v !== null);
      if (!values.length) return null;
      return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
    })();

    return { total, byStatus, avgOpenDays };
  }, [sortedData]);

  const openRequest = (request: AssetRequest) => {
    setSelectedRequest(request);
    setEditDraft({ ...request });
  };

  const closeRequest = () => {
    if (saving) return;
    setSelectedRequest(null);
    setEditDraft(null);
  };

  const updateDraftField = <K extends keyof AssetRequest>(
    field: K,
    value: AssetRequest[K],
  ) => {
    setEditDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const saveRequestUpdates = async () => {
    if (!selectedRequest || !editDraft) return;

    setSaving(true);
    try {
      const payload = {
        candidate_id: editDraft.candidate_id,
        requested_asset_type: editDraft.requested_asset_type,
        ship_to_name: editDraft.ship_to_name,
        ship_to_email: editDraft.ship_to_email,
        ship_to_company: editDraft.ship_to_company,
        shipping_address1: editDraft.shipping_address1,
        shipping_address2: editDraft.shipping_address2,
        shipping_city: editDraft.shipping_city,
        shipping_state: editDraft.shipping_state,
        shipping_postal_code: editDraft.shipping_postal_code,
        shipping_country: editDraft.shipping_country,
        requested_by_email: editDraft.requested_by_email,
        needed_by_date: editDraft.needed_by_date,
        status: editDraft.status,
        shipping_notes: editDraft.shipping_notes,
        intune_completed: editDraft.intune_completed,
        device_ownership_type: editDraft.device_ownership_type,
        ct_verified: editDraft.ct_verified,
        service_line: editDraft.service_line,
        scheduled_delivery_date: editDraft.scheduled_delivery_date,
      };

      await axios.put(`/asset-requests/${selectedRequest.request_id}`, payload);

      const updatedAt = new Date().toISOString();

      const savedRow: AssetRequest = {
        ...editDraft,
        updated_at: updatedAt,
      };

      setData((prev) =>
        (prev ?? []).map((row) =>
          row.request_id === selectedRequest.request_id ? savedRow : row,
        ),
      );

      setSelectedRequest(savedRow);
      setEditDraft(savedRow);
    } catch (err: any) {
      console.error("Error updating asset request:", err);
      alert(err?.message ?? "Failed to update asset request");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <div>Pulling Asset Requests from the database…</div>
        <Spinner color='primary' />
      </>
    );
  }

  if (error) return <div>Error: {error}</div>;

  return (
    <Col xs='12'>
      <style>{`
        .ar-table {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
          font-size: 1rem;
          text-align: left;
        }

        .ar-table th, .ar-table td {
          padding: 12px 15px;
          border: 1px solid #ddd;
          vertical-align: top;
        }

        .ar-table th {
          background-color: #f4f4f4;
          font-weight: bold;
          cursor: pointer;
        }

        .even-row { background-color: #f9f9f9; }
        .odd-row { background-color: #fff; }

        .ar-table tr:hover {
          background-color: #f1f1f1;
          cursor: pointer;
        }

        .muted { color: #666; }

        .chip {
          display: inline-block;
          padding: 2px 10px;
          border-radius: 999px;
          background: #eee;
          font-size: 0.85rem;
          white-space: nowrap;
        }

        .readonly-value {
          padding: 10px 12px;
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          border-radius: 6px;
          min-height: 42px;
        }

        .summary-card {
          padding: 16px;
          border: 1px solid #ddd;
          border-radius: 8px;
          background: #fafafa;
          margin-bottom: 20px;
        }

        .summary-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 12px;
        }

        .detail-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .detail-grid-full {
          grid-column: 1 / -1;
        }

        @media (max-width: 768px) {
          .detail-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <Row className='align-items-center mb-3'>
        <Col xs='12' md='6'>
          <H3 className='font-primary'>{reportTitle}</H3>
        </Col>
        <Col xs='12' md='6' className='text-md-end mt-2 mt-md-0'>
          <Button color='success' onClick={downloadCsv}>
            Download CSV
          </Button>
        </Col>
      </Row>

      <Row>
        <Col xs='12'>
          <div className='summary-card'>
            <div className='summary-grid'>
              <div>
                <strong>Total Requests</strong>
                <div>{summary.total}</div>
              </div>
              <div>
                <strong>Avg Open Days</strong>
                <div>{summary.avgOpenDays ?? "—"}</div>
              </div>
              <div>
                <strong>Status Breakdown</strong>
                <div className='muted'>
                  {Object.entries(summary.byStatus)
                    .map(([status, count]) => `${status}: ${count}`)
                    .join(" | ") || "—"}
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col xs='12'>
          <table className='ar-table'>
            <thead>
              <tr>
                <th onClick={() => sortData("request_id")}>Request ID</th>
                <th onClick={() => sortData("created_at")}>Created</th>
                <th onClick={() => sortData("status")}>Status</th>
                <th onClick={() => sortData("requested_asset_type")}>
                  Asset Type
                </th>
                <th onClick={() => sortData("ship_to_name")}>Ship To</th>
                <th onClick={() => sortData("requested_by_email")}>
                  Requested By
                </th>
                <th onClick={() => sortData("needed_by_date")}>Needed By</th>
                <th onClick={() => sortData("intune_completed")}>Intune</th>
                <th onClick={() => sortData("device_ownership_type")}>
                  Ownership
                </th>
                <th onClick={() => sortData("ct_verified")}>CT Verified</th>
                <th onClick={() => sortData("service_line")}>Service Line</th>
                <th>Shipping Notes</th>
                <th onClick={() => sortData("scheduled_delivery_date")}>
                  Scheduled Delivery
                </th>
              </tr>
            </thead>

            <tbody>
              {sortedData.map((r, index) => {
                const created = parseDate(r.created_at);
                const ageDays = daysBetween(created, new Date());

                return (
                  <tr
                    key={r.request_id}
                    className={index % 2 === 0 ? "even-row" : "odd-row"}
                    onClick={() => openRequest(r)}
                  >
                    <td>{r.request_id}</td>
                    <td>
                      <div>{r.created_at ?? "—"}</div>
                      {ageDays !== null ? (
                        <div className='muted'>Open: {ageDays}d</div>
                      ) : null}
                    </td>
                    <td>
                      <span className='chip'>{r.status ?? "—"}</span>
                    </td>
                    <td>{r.requested_asset_type ?? "—"}</td>
                    <td>{r.ship_to_name ?? "—"}</td>
                    <td>{r.requested_by_email ?? "—"}</td>
                    <td>{r.needed_by_date ?? "—"}</td>
                    <td>{formatIntBool(r.intune_completed)}</td>
                    <td>{r.device_ownership_type ?? "—"}</td>
                    <td>{formatIntBool(r.ct_verified)}</td>
                    <td>{r.service_line ?? "—"}</td>
                    <td style={{ maxWidth: 280 }}>
                      <div className='muted'>
                        {r.shipping_notes ? r.shipping_notes : "—"}
                      </div>
                    </td>
                    <td>{r.scheduled_delivery_date ?? "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Col>
      </Row>

      <Modal isOpen={!!selectedRequest} toggle={closeRequest} size='lg'>
        <ModalHeader toggle={closeRequest}>
          Asset Request #{editDraft?.request_id}
        </ModalHeader>

        <ModalBody>
          {editDraft && (
            <div className='detail-grid'>
              <FormGroup>
                <Label>Request ID</Label>
                <div className='readonly-value'>{editDraft.request_id}</div>
              </FormGroup>

              <FormGroup>
                <Label>Status</Label>
                <Input
                  type='select'
                  value={editDraft.status ?? ""}
                  onChange={(e) => updateDraftField("status", e.target.value)}
                >
                  <option value=''>Select...</option>
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </Input>
              </FormGroup>

              <FormGroup>
                <Label>Candidate ID</Label>
                <Input
                  type='number'
                  value={editDraft.candidate_id ?? ""}
                  onChange={(e) =>
                    updateDraftField(
                      "candidate_id",
                      e.target.value === "" ? null : Number(e.target.value),
                    )
                  }
                />
              </FormGroup>

              <FormGroup>
                <Label>Requested Asset Type</Label>
                <Input
                  type='select'
                  value={editDraft.requested_asset_type ?? ""}
                  onChange={(e) =>
                    updateDraftField("requested_asset_type", e.target.value)
                  }
                >
                  <option value=''>Select...</option>
                  {ASSET_TYPE_OPTIONS.map((assetType) => (
                    <option key={assetType} value={assetType}>
                      {assetType}
                    </option>
                  ))}
                </Input>
              </FormGroup>

              <FormGroup>
                <Label>Ship To Name</Label>
                <Input
                  type='text'
                  value={editDraft.ship_to_name ?? ""}
                  onChange={(e) =>
                    updateDraftField("ship_to_name", e.target.value)
                  }
                />
              </FormGroup>

              <FormGroup>
                <Label>Ship To Email</Label>
                <Input
                  type='email'
                  value={editDraft.ship_to_email ?? ""}
                  onChange={(e) =>
                    updateDraftField("ship_to_email", e.target.value)
                  }
                />
              </FormGroup>

              <FormGroup>
                <Label>Ship To Company</Label>
                <Input
                  type='text'
                  value={editDraft.ship_to_company ?? ""}
                  onChange={(e) =>
                    updateDraftField("ship_to_company", e.target.value)
                  }
                />
              </FormGroup>

              <FormGroup>
                <Label>Requested By Email</Label>
                <Input
                  type='email'
                  value={editDraft.requested_by_email ?? ""}
                  onChange={(e) =>
                    updateDraftField("requested_by_email", e.target.value)
                  }
                />
              </FormGroup>

              <FormGroup className='detail-grid-full'>
                <Label>Shipping Address 1</Label>
                <Input
                  type='text'
                  value={editDraft.shipping_address1 ?? ""}
                  onChange={(e) =>
                    updateDraftField("shipping_address1", e.target.value)
                  }
                />
              </FormGroup>

              <FormGroup className='detail-grid-full'>
                <Label>Shipping Address 2</Label>
                <Input
                  type='text'
                  value={editDraft.shipping_address2 ?? ""}
                  onChange={(e) =>
                    updateDraftField("shipping_address2", e.target.value)
                  }
                />
              </FormGroup>

              <FormGroup>
                <Label>City</Label>
                <Input
                  type='text'
                  value={editDraft.shipping_city ?? ""}
                  onChange={(e) =>
                    updateDraftField("shipping_city", e.target.value)
                  }
                />
              </FormGroup>

              <FormGroup>
                <Label>State</Label>
                <Input
                  type='text'
                  value={editDraft.shipping_state ?? ""}
                  onChange={(e) =>
                    updateDraftField("shipping_state", e.target.value)
                  }
                />
              </FormGroup>

              <FormGroup>
                <Label>Postal Code</Label>
                <Input
                  type='text'
                  value={editDraft.shipping_postal_code ?? ""}
                  onChange={(e) =>
                    updateDraftField("shipping_postal_code", e.target.value)
                  }
                />
              </FormGroup>

              <FormGroup>
                <Label>Country</Label>
                <Input
                  type='text'
                  value={editDraft.shipping_country ?? ""}
                  onChange={(e) =>
                    updateDraftField("shipping_country", e.target.value)
                  }
                />
              </FormGroup>

              <FormGroup>
                <Label>Needed By Date</Label>
                <Input
                  type='date'
                  value={editDraft.needed_by_date ?? ""}
                  onChange={(e) =>
                    updateDraftField("needed_by_date", e.target.value || null)
                  }
                />
              </FormGroup>

              <FormGroup>
                <Label>Scheduled Delivery Date</Label>
                <Input
                  type='date'
                  value={editDraft.scheduled_delivery_date ?? ""}
                  onChange={(e) =>
                    updateDraftField(
                      "scheduled_delivery_date",
                      e.target.value || null,
                    )
                  }
                />
              </FormGroup>

              <FormGroup>
                <Label>Created At</Label>
                <div className='readonly-value'>
                  {editDraft.created_at ?? "—"}
                </div>
              </FormGroup>

              <FormGroup>
                <Label>Updated At</Label>
                <div className='readonly-value'>
                  {editDraft.updated_at ?? "—"}
                </div>
              </FormGroup>

              <FormGroup>
                <Label check>
                  <Input
                    type='checkbox'
                    checked={Number(editDraft.intune_completed) === 1}
                    onChange={(e) =>
                      updateDraftField(
                        "intune_completed",
                        e.target.checked ? 1 : 0,
                      )
                    }
                  />{" "}
                  Intune Completed
                </Label>
              </FormGroup>

              <FormGroup>
                <Label>Device Ownership</Label>
                <Input
                  type='select'
                  value={editDraft.device_ownership_type ?? ""}
                  onChange={(e) =>
                    updateDraftField("device_ownership_type", e.target.value)
                  }
                >
                  <option value=''>Select...</option>
                  {OWNERSHIP_OPTIONS.map((ownershipType) => (
                    <option key={ownershipType} value={ownershipType}>
                      {ownershipType}
                    </option>
                  ))}
                </Input>
              </FormGroup>

              <FormGroup>
                <Label check>
                  <Input
                    type='checkbox'
                    checked={Number(editDraft.ct_verified) === 1}
                    onChange={(e) =>
                      updateDraftField("ct_verified", e.target.checked ? 1 : 0)
                    }
                  />{" "}
                  CT Verified
                </Label>
              </FormGroup>

              <FormGroup>
                <Label>Service Line</Label>
                <Input
                  type='select'
                  value={editDraft.service_line ?? ""}
                  onChange={(e) =>
                    updateDraftField("service_line", e.target.value)
                  }
                >
                  <option value=''>Select...</option>
                  {SERVICE_LINE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Input>
              </FormGroup>

              <FormGroup className='detail-grid-full'>
                <Label for='shippingInfo'>Shipping Info</Label>
                <Input
                  id='shippingInfo'
                  type='textarea'
                  rows={6}
                  value={editDraft.shipping_notes ?? ""}
                  onChange={(e) =>
                    updateDraftField("shipping_notes", e.target.value)
                  }
                  placeholder='Enter shipping/tracking details, carrier notes, shipment status, etc.'
                />
              </FormGroup>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button color='secondary' onClick={closeRequest} disabled={saving}>
            Close
          </Button>
          <Button
            color='primary'
            onClick={saveRequestUpdates}
            disabled={saving || !editDraft}
          >
            {saving ? "Saving..." : "Save Updates"}
          </Button>
        </ModalFooter>
      </Modal>
    </Col>
  );
};

export default AssetRequestsReport;
